import React, { useEffect } from 'react';
import style from './SelfSchedule.module.scss';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import ThankYouScreen from './components/ThankYouScreen';
import { useSelfSchedule } from './hooks/useSelfSchedule';
import HRAvailability from './components/HRAvailability';
import { Alert, Body, Button, Display } from '@walmart-web/livingdesign-components';
import { loadData } from 'redux/SelfSchedule/request';
import { useMediaQuery } from 'react-responsive';
import HRAvailabilitySC from './components/HRAvailabilitySC';
import SelfScheduleTourFooter from './components/SelfScheduleTourFooter';
import { ReactComponent as Clock } from '@livingdesign/pictograms/dist/svg/Home/Clock.svg';
import { selfScheduleEventCodeMap } from 'config/const';
import { useTranslation } from 'react-i18next';
import { formatPhoneNumber, resolveStoreName } from './utils/formatUtils';

const Icon = require('@livingdesign/icons');

const SelfSchedule = () => {
	const { encId } = useParams();
	const location = useLocation();
	const selfSchedule = useSelector((state) => state.selfSchedule);
	const global = useSelector((state) => state.global);
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const isMobile = useMediaQuery({ maxWidth: 425 });
	const { t, i18n } = useTranslation();
	const isPrescreenEvent = selfSchedule.eventCode === selfScheduleEventCodeMap.SH_PRESCREEN_EVENT_CODE;
	const isJobPreviewCandidate = isPrescreenEvent && !!selfSchedule.isJobPreviewCandidate;
	const isJobPreviewReschedule = isJobPreviewCandidate && !!selfSchedule.isReschedule;
	const formattedStorePhone = formatPhoneNumber(selfSchedule.storePhoneNumber);
	const storeNbr = selfSchedule.storeNbr || selfSchedule.responseData?.storeNbr;
	const storeName = resolveStoreName({ storeNbr, storeName: selfSchedule.storeName, t });

	const {
		displaySeeMore,
		interviewConfirmed,
		introText,
		subText,
		isSelfScheduleSC,
		storeAddress,
		handleToggleSeeMore,
		setDisplaySeeMore,
		setInterviewConfirmed,
		setStatesForSH,
		setStatesForSC
	} = useSelfSchedule();

	useEffect(() => {
		const queryParams = new URLSearchParams(location.search);
		const redirectUrl = queryParams.get('redirectUrl');
		const languagePref = queryParams.get('lang');
		redirectUrl && sessionStorage.setItem("ssRedirectUrl", redirectUrl);
		languagePref && sessionStorage.setItem("languagePref", languagePref)
		if (global && global.error.message) {
			navigate('../error');
		}
	}, [global, global.error.message, location.search, navigate]);
	useEffect(()=>{
		i18n.changeLanguage(
			(sessionStorage.getItem('languagePref') || 'EN').toLowerCase()
		);
	});
	useEffect(() => {
		if (!selfSchedule.dataLoaded) {
			dispatch(loadData(encId));
		}
	}, [encId]);

	useEffect(() => {
		setDisplaySeeMore(!isSelfScheduleSC && selfSchedule.displaySeeMore);
	}, [selfSchedule.displaySeeMore, isSelfScheduleSC]);

	useEffect(() => {
		setInterviewConfirmed(selfSchedule.interviewConfirmed);
	}, [selfSchedule.interviewConfirmed]);

	useEffect(() => {
		if (selfSchedule.dataLoaded) {
			if (
				selfSchedule.eventCode === selfScheduleEventCodeMap.SC_INTERVIEW_EVENT_CODE ||
				selfSchedule.eventCode === selfScheduleEventCodeMap.SC_ORIENTATION_EVENT_CODE_2 ||
				selfSchedule.eventCode === selfScheduleEventCodeMap.SC_ORIENTATION_EVENT_CODE_1 ||
				selfSchedule.eventCode === selfScheduleEventCodeMap.SC_RJPT_EVENT_CODE ||
				selfSchedule.eventCode === selfScheduleEventCodeMap.SC_FT_EVENT_CODE
			) {
				setStatesForSC();
			} else {
				setStatesForSH();
			}
		}
	}, [selfSchedule.dataLoaded]);

	if (interviewConfirmed) {
		return <ThankYouScreen />;
	}
	if (!selfSchedule.dataLoaded) {
		return <></>;
	}

	return (
		<div className={style.Container}>
			<div>
				<div
					className={`${style.Self_Schedule} ${
						isJobPreviewCandidate ? style.jobPreview_header : ''
					}`}>
					<Display UNSAFE_className={style.Intro_Text} as="h1" size="medium" weight={400}>
						{t(introText)}
					</Display>
					{isMobile && isSelfScheduleSC ? null : (
						<Body as="p" size="medium" weight={400} UNSAFE_className={style.Intro_Subtext}>
							{isJobPreviewCandidate
								? t(subText)
								: subText
								? subText
										.split(',')
										.filter(Boolean)
										.map((key) => t(key))
										.join(' ')
								: ''}
						</Body>
					)}
					<div className={isJobPreviewCandidate ? style.jobPreview_card_wrap : undefined}>
						<div
							className={`${style.slot_time_n_address} ${
								isJobPreviewCandidate ? style.jobPreview_info_card : ''
							}`}>
							<div className={style.slot_time}>
								<div style={{ display: 'flex' }}>
									<Clock
										style={
											isJobPreviewCandidate
												? { width: '24px', height: '24px' }
												: { width: '30px', height: '30px' }
										}
									/>
								</div>
								{isJobPreviewCandidate ? null : <>&nbsp;</>}
								<div>
									<div>
										{isJobPreviewCandidate
											? t('selfSchedule.jobPreviewDuration')
											: t(selfSchedule.duration)}
									</div>
									{isSelfScheduleSC && (
										<div className={style.duration_subtext}>{t('selfSchedule.dependent')}</div>
									)}
								</div>
							</div>
							<div className={style.address}>
								<div>
									<Icon.Location size="medium" />
								</div>
								{isJobPreviewCandidate ? null : <>&nbsp;</>}
								<div>
									{isJobPreviewCandidate ? (
										<div>
											{storeName ? <div>{storeName}</div> : null}
											<div>{storeAddress}</div>
											{formattedStorePhone ? <div>{formattedStorePhone}</div> : null}
										</div>
									) : (
										isPrescreenEvent
											? `${t(storeAddress.split(',')[0])} ${storeAddress.split(',')[1]}`
											: storeAddress
									)}
								</div>
							</div>
						</div>
						{isJobPreviewReschedule ? (
							<div className={style.jobPreview_reschedule_banner}>
								<Alert variant="info">{t('selfSchedule.jobPreviewRescheduleBanner')}</Alert>
							</div>
						) : null}
					</div>
				</div>
			</div>
			<div className={style.availabilityContainer}>
				{isSelfScheduleSC ? <HRAvailabilitySC /> : <HRAvailability />}
			</div>
			<div className={style.footer_content}>
				<div>
					{displaySeeMore ? (
						<div className={style.footer_button}>
							<Button
								size="medium"
								variant="primary"
								style={{ backgroundColor: 'none' }}
								onClick={handleToggleSeeMore}>
								{selfSchedule.seeMore ? (
									<>{t('selfSchedule.seeMore')}</>
								) : (
									<>{t('selfSchedule.seeLess')}</>
								)}
							</Button>
						</div>
					) : (
						''
					)}
				</div>
				<div>
					{isSelfScheduleSC ? (
						<SelfScheduleTourFooter />
					) : (
						<Body as="p" size="medium" weight={400} UNSAFE_className={style.footer_text}>
							{t('selfSchedule.noDatesToChoose')}
						</Body>
					)}
				</div>
			</div>
		</div>
	);
};

export default SelfSchedule;
